import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  InputAdornment,
  Radio,
  CircularProgress,
  MenuItem,
  InputBase,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  searchPartnerToLink,
  linkPartnerOrPlanner,
} from "../../../../../api/admin/provider-management";

interface Props {
  open: boolean;
  onClose: () => void;
  sourceId: number;
  onSuccess?: () => void;
  onError?: (msg: string) => void;
}

const LinkAccountPopup: React.FC<Props> = ({
  open,
  onClose,
  sourceId,
  onSuccess,
  onError,
}) => {
  const [searchType, setSearchType] = useState<
    "email" | "phone" | "company" | ""
  >("");
  const [searchValue, setSearchValue] = useState("");
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [countryCode, setCountryCode] = useState("+1");

  const validateInput = (type: string, value: string) => {
    if (!value.trim()) return "This field is required";

    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return "Enter a valid email";
    }

    if (type === "phone") {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(value.replace(/\s+/g, ""))) {
        return "Enter a valid 10-digit phone number";
      }
    }

    if (type === "company" && value.trim().length < 3) {
      return "Company name must be at least 3 characters";
    }

    return "";
  };

  const handleSearch = async () => {
    if (!searchValue || !searchType) return;

    const error = validateInput(searchType, searchValue);
    if (error) {
      setInputError(error);
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      setInputError("");
      const valueToSend =
        searchType === "phone" ? `${countryCode} ${searchValue}` : searchValue;

      const res = await searchPartnerToLink({
        type: searchType,
        value: valueToSend,
      });

      setResult(res?.data?.data?.[0] || null);
    } catch (error: any) {
      setResult(null);
      onError?.(
        error?.response?.data?.message ||
          "Something went wrong while searching",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchType("");
      setSearchValue("");
      setResult(null);
      setInputError("");
      setLoading(false);
      setHasSearched(false);
      setCountryCode("+1");
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!result) return;

    try {
      await linkPartnerOrPlanner(sourceId, {
        id: result.id,
        companyName: result.companyName,
        email: result.email,
        phoneNumber: result.phoneNumber,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      onError?.(
        error?.response?.data?.message ||
          "Failed to link partner. Please try again.",
      );
    }
  };

  const getPlaceholder = () => {
    if (searchType === "email") return "Enter email";
    if (searchType === "phone") return "Enter phone number";
    if (searchType === "company") return "Enter company name";
    return "";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          height: "500px",
          "& .Mui-focused": {
            backgroundColor: "transparent !important",
          },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "18px", fontWeight: 800, px: 3, py: 2 }}>
        Link Partner
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              color: "#374151",
              mb: 1,
              fontWeight: 600,
            }}
          >
            Search partner with
          </Typography>

          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
            {[
              { label: "Company Name", value: "company" },
              { label: "Email", value: "email" },
              { label: "Phone Number", value: "phone" },
            ].map((option) => (
              <Box
                key={option.value}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Radio
                  checked={searchType === option.value}
                  onChange={() => {
                    setSearchType(option.value as any);
                    setSearchValue("");
                    setResult(null);
                    setInputError("");
                    setHasSearched(false);
                  }}
                  sx={{
                    color: "#009689",
                    "&.Mui-checked": { color: "#009689" },
                    "&:hover": {
                      backgroundColor: "rgba(0, 150, 137, 0.08)",
                    },
                  }}
                />
                <Typography sx={{ fontSize: "14px" }}>
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {searchType === "phone" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                height: "42px",
                px: 1.5,
                mb: 1,
                backgroundColor: "#fff",
              }}
            >
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                  minWidth: "70px",
                  fontSize: "13px",
                  mr: 1,
                  "& .MuiSelect-select": {
                    padding: "4px 6px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="+1">
                  <img
                    src="https://flagcdn.com/us.svg"
                    width="20"
                    style={{ marginRight: 8 }}
                  />
                  +1
                </MenuItem>

                <MenuItem value="+91">
                  <img
                    src="https://flagcdn.com/in.svg"
                    width="20"
                    style={{ marginRight: 8 }}
                  />
                  +91
                </MenuItem>

                <MenuItem value="+44">
                  <img
                    src="https://flagcdn.com/gb.svg"
                    width="20"
                    style={{ marginRight: 8 }}
                  />
                  +44
                </MenuItem>
              </Select>

              <InputBase
                fullWidth
                placeholder="Enter phone number"
                value={searchValue}
                autoComplete="off"
                error={!!inputError}
                inputProps={{ autoComplete: "new-password" }}
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 10);
                  setSearchValue(cleaned);

                  const error = validateInput("phone", cleaned);
                  setInputError(error);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                sx={{
                  fontSize: "14px",
                }}
              />

              <SearchIcon
                onClick={handleSearch}
                sx={{
                  color: "#9CA3AF",
                  fontSize: "20px",
                  cursor: "pointer",
                  ml: 1,
                }}
              />
            </Box>
          ) : searchType ? (
            <TextField
              fullWidth
              name="search"
              placeholder={getPlaceholder()}
              value={searchValue}
              error={!!inputError}
              helperText={searchValue ? inputError : ""}
              autoComplete="off"
              inputProps={{ autoComplete: "new-password" }}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);

                const error = validateInput(searchType, val);
                setInputError(error);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      onClick={handleSearch}
                      sx={{
                        color: "#9CA3AF",
                        fontSize: "20px",
                        cursor: "pointer",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiInputBase-root": {
                  borderRadius: "10px",
                  fontSize: "14px",
                  height: "42px",
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                  WebkitTextFillColor: "#000 !important",
                  caretColor: "#000",
                },
              }}
            />
          ) : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <CircularProgress />
            </Box>
          ) : result ? (
            <Box
              sx={{
                maxHeight: "260px",
                overflowY: "auto",
                border: "1px solid #E5E7EB",
                borderRadius: "2px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 2fr 1.2fr",
                  px: 2,
                  py: 1.5,
                  backgroundColor: "#F3F4F6",
                  borderBottom: "1px solid #E5E7EB",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                <Box>Company Name</Box>
                <Box>Email</Box>
                <Box>Phone</Box>
              </Box>

              {result && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 2fr 1.2fr",
                    alignItems: "center",
                    px: 2,
                    py: 1.4,
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#111827",
                    }}
                  >
                    {result.companyName}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#6B7280",
                      wordBreak: "break-word",
                    }}
                  >
                    {result.email}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#6B7280",
                    }}
                  >
                    {result.phoneNumber || "-"}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            hasSearched && (
              <Typography
                sx={{
                  textAlign: "center",
                  mt: 5,
                  fontSize: "13px",
                  color: "#9CA3AF",
                }}
              >
                No matching partner found
              </Typography>
            )
          )}
        </Box>

        <Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                fontSize: "13px",
                color: "#374151",
                borderColor: "#D1D5DB",
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={!result}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                px: 3,
                py: 1,
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: "#E5C07B",
                color: "#000",
                "&:hover": {
                  backgroundColor: "#D4AF6A",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              Confirm Link
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LinkAccountPopup;
